/* global NDEFReader */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { jwtDecode } from 'jwt-decode';

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './BitacoraPage.css';

const BitacoraPage = () => {
  const [registros, setRegistros] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8;
  const [selectedRegistro, setSelectedRegistro] = useState(null);

  const [showEntradaModal, setShowEntradaModal] = useState(false);
  const [showSalidaModal, setShowSalidaModal] = useState(false);

  // Estados para evitar llamadas duplicadas (procesamiento)
  const [isProcessingEntrada, setIsProcessingEntrada] = useState(false);
  const [isProcessingSalida, setIsProcessingSalida] = useState(false);

  const token = useSelector((state) => state.auth.token);
  const decoded = token ? jwtDecode(token) : null;
  const estacionUsuario = decoded?.estacion?.trim().toUpperCase();

  useEffect(() => {
    const fetchRegistros = async () => {
      if (!token || !estacionUsuario) return;
      try {
        const response = await axios.get(
          'https://orionadministracion.com/api/bitacora/dia',
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
  
        const data = response.data || [];
  
        const registrosFiltrados = data.filter(
          (r) => r.estacion?.trim().toUpperCase() === estacionUsuario
        );
  
        setRegistros(registrosFiltrados);
      } catch (error) {
        console.error('Error al obtener los registros:', error);
      }
    };
  
    fetchRegistros();
  }, [token, estacionUsuario]);
  
  

  // Filtrado por No. Colaborador o Siglas (buscador)
  const filteredRegistros = registros.filter((registro) => {
    const searchLower = searchQuery.toLowerCase();
    const noColaborador = registro.noColaborador
      ? registro.noColaborador.toString().toLowerCase()
      : '';
    const siglas = registro.siglas ? registro.siglas.toLowerCase() : '';
    return noColaborador.includes(searchLower) || siglas.includes(searchLower);
  });

  const totalPages = Math.ceil(filteredRegistros.length / pageSize);
  const currentRecords = filteredRegistros.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  /* Lógica NFC para Registro de Entrada */
  useEffect(() => {
    if (showEntradaModal) {
      // Reiniciamos el estado para evitar lecturas anteriores
      setIsProcessingEntrada(false);
      if ('NDEFReader' in window) {
        const reader = new NDEFReader();
        const startNFC = async () => {
          try {
            await reader.scan();
            toast.info('Acerque su tarjeta NFC...', { autoClose: 1000 });
            reader.onreading = async (event) => {
              // Si ya se está procesando, se ignora la lectura
              if (isProcessingEntrada) return;
              if (event.message.records && event.message.records.length > 0) {
                const record = event.message.records[0];
                const nfcData = record.data
                  ? new TextDecoder().decode(record.data)
                  : 'Datos no disponibles';
                //toast.info(`Datos leídos de NFC: ${nfcData}`, { autoClose: 2000 });
                handleScanEntrada(nfcData);
              } else {
                toast.warn('No se encontraron registros en el mensaje NFC', {
                  autoClose: 1000,
                });
                setTimeout(() => {
                  setShowEntradaModal(false);
                }, 1000);
              }
            };
          } catch (error) {
            toast.error('Error al activar el lector NFC', { autoClose: 1000 });
            setTimeout(() => {
              setShowEntradaModal(false);
            }, 1000);
          }
        };
        startNFC();
      } else {
        toast.warn('NFC no es compatible con este dispositivo.', { autoClose: 1000 });
        setTimeout(() => {
          setShowEntradaModal(false);
        }, 1000);
      }
    }
  }, [showEntradaModal]);

  const handleScanEntrada = async (nfc_id) => {
    if (!token) {
      toast.error('Error: Token no encontrado.', { autoClose: 1000 });
      setTimeout(() => {
        setShowEntradaModal(false);
      }, 1000);
      return;
    }
    try {
      const response = await axios.post(
        'https://orionadministracion.com/api/bitacora',
        { nfc_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Registro exitoso', { autoClose: 1000 });
      setTimeout(() => {
        setShowEntradaModal(false);
        window.location.reload();
      }, 1000);
    } catch (error) {
      const errorMsg = error.response
        ? error.response.data.error || 'Error en el servidor'
        : 'Error en el servidor';
      toast.error(errorMsg, { autoClose: 1000 });
      setTimeout(() => {
        setShowEntradaModal(false);
      }, 1000);
    }
  };

  /* Lógica NFC para Registro de Salida */
  useEffect(() => {
    if (showSalidaModal) {
      // Reiniciamos el estado para evitar lecturas anteriores
      setIsProcessingSalida(false);
      if ('NDEFReader' in window) {
        const reader = new NDEFReader();
        const startNFC = async () => {
          try {
            await reader.scan();
            toast.info('Acerque su tarjeta NFC...', { autoClose: 1000 });
            reader.onreading = async (event) => {
              if (isProcessingSalida) return;
              if (event.message.records && event.message.records.length > 0) {
                const record = event.message.records[0];
                const nfcData = record.data
                  ? new TextDecoder().decode(record.data)
                  : 'Datos no disponibles';
                //toast.info(`Datos leídos de NFC: ${nfcData}`, { autoClose: 2000 });
                handleScanSalida(nfcData);
              } else {
                toast.warn('No se encontraron registros en el mensaje NFC', {
                  autoClose: 1000,
                });
                setTimeout(() => {
                  setShowSalidaModal(false);
                }, 1000);
              }
            };
          } catch (error) {
            toast.error('Error al activar el lector NFC', { autoClose: 1000 });
            setTimeout(() => {
              setShowSalidaModal(false);
            }, 1000);
          }
        };
        startNFC();
      } else {
        toast.warn('NFC no es compatible con este dispositivo.', { autoClose: 1000 });
        setTimeout(() => {
          setShowSalidaModal(false);
        }, 1000);
      }
    }
  }, [showSalidaModal]);

  const handleScanSalida = async (nfc_id) => {
    if (!token) {
      toast.error('Error: Token no encontrado.', { autoClose: 1000 });
      setTimeout(() => {
        setShowSalidaModal(false);
      }, 1000);
      return;
    }
    try {
      const response = await axios.put(
        'https://orionadministracion.com/api/bitacora/salida',
        { nfc_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Salida registrada exitosamente', { autoClose: 1000 });
      setTimeout(() => {
        setShowSalidaModal(false);
        window.location.reload();
      }, 1000);
    } catch (error) {
      const errorMsg = error.response
        ? error.response.data.error || 'Error en el servidor'
        : 'Error en el servidor';
      toast.error(errorMsg, { autoClose: 1000 });
      setTimeout(() => {
        setShowSalidaModal(false);
        window.location.reload();
      }, 1000);
    }
  };

  // Handlers de paginación
  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  return (
    
    <div className="bitacora-container">
      <h3>Estación actual: {estacionUsuario}</h3>

      <h1>Bitácora de Operadores</h1>
      <div className="botones-registro">
        <button className="registro-button" onClick={() => {setShowEntradaModal(true); setIsProcessingEntrada(false)}}>
          Registrar Entrada
        </button>
        <button className="registro-button salida" onClick={() => {setShowSalidaModal(true); setIsProcessingSalida(false)}}>
          Registrar Salida
        </button>
      </div>

      {/* Buscador */}
      <div className="buscador-container">
        <input
          type="text"
          placeholder="Buscar por No. Colaborador o Siglas..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
        />
      </div>

      {/* Listado de tarjetas compactas */}
      <div className="tarjetas-container">
        {currentRecords.length === 0 ? (
          <p>No hay registros para mostrar.</p>
        ) : (
          currentRecords.map((registro, index) => (
            <div
              key={index}
              className="tarjeta-compacta"
              onClick={() => setSelectedRegistro(registro)}
            >
              <span>{registro.noFila || 'N/A'}</span> -{' '}
              <span>
                {registro.noColaborador || 'N/A'}{' '}
                {registro.siglas ? `(${registro.siglas})` : ''}
              </span>{' '}
              - <span>{registro.status || 'N/A'}</span>
            </div>
          ))
        )}
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="paginacion">
          <button onClick={handlePrevPage} disabled={currentPage === 1}>
            {'<'}
          </button>
          <span>
            Página {currentPage} de {totalPages}
          </span>
          <button onClick={handleNextPage} disabled={currentPage === totalPages}>
            {'>'}
          </button>
        </div>
      )}

      {/* Modal de Detalle del Registro */}
      {selectedRegistro && (
        <div className="modal" onClick={() => setSelectedRegistro(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Detalle del Registro</h2>
            <div className="detalle-info">
              <p>
                <strong>No. Fila:</strong> {selectedRegistro.noFila || 'N/A'}
              </p>
              <p>
                <strong>No. Colaborador:</strong>{' '}
                {selectedRegistro.noColaborador || 'N/A'}{' '}
                {selectedRegistro.siglas && `(${selectedRegistro.siglas})`}
              </p>
              <p>
                <strong>Entrada:</strong>{' '}
                {selectedRegistro.entrada
                  ? new Date(selectedRegistro.entrada).toLocaleString()
                  : 'No registrada'}
              </p>
              <p>
                <strong>Salida:</strong>{' '}
                {selectedRegistro.salida
                  ? new Date(selectedRegistro.salida).toLocaleString()
                  : 'No registrada'}
              </p>
              <p>
                <strong>Registrado Por:</strong> {selectedRegistro.register_by || 'N/A'}
              </p>
              <p>
                <strong>Último Servicio:</strong> {selectedRegistro.ultimoServicio || 'N/A'}
              </p>
              <p>
                <strong>Status:</strong> {selectedRegistro.status || 'N/A'}
              </p>
            </div>
            <div className="modal-actions">
              <button onClick={() => setSelectedRegistro(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para Registro de Entrada (NFC) */}
      {showEntradaModal && (
        <div className="modal" onClick={() => setShowEntradaModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Registro de Entrada</h2>
            {/* No se muestran mensajes aquí; se usan toast */}
          </div>
        </div>
      )}

      {/* Modal para Registro de Salida (NFC) */}
      {showSalidaModal && (
        <div className="modal" onClick={() => setShowSalidaModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Registro de Salida</h2>
            {/* No se muestran mensajes aquí; se usan toast */}
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default BitacoraPage;
